import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import Header from "../../components/Header";
import BackButton from "../../components/BackButton";
import Loading from "../../components/Loading";
import ErrorComponent from "../../components/Error";
import DOMPurify from "dompurify";

const ExerciseDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState<any>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchExercise = async () => {
      try {
        const fetchedExercise = location.state?.exercise;
        if (!fetchedExercise) {
          throw new Error("Exercise not found");
        }
        setExercise(fetchedExercise);
      } catch (err: any) {
        setError(err.message || "Failed to fetch exercise details");
      } finally {
        setLoading(false);
      }
    };

    fetchExercise();
  }, [location.state]);

  if (loading) {
    return <Loading message="Loading exercise details..." />;
  }

  if (error) {
    return (
      <ErrorComponent
        message={error}
        onRetry={() => {
          setLoading(true);
          setError(null);
          setTimeout(() => setLoading(false), 1000);
        }}
      />
    );
  }

  if (!exercise) {
    return (
      <ErrorComponent
        message="Exercise not found"
        onRetry={() => navigate("/exercises")}
      />
    );
  }

  const primaryMuscles =
    exercise.primaryTargetMuscles?.map((m: any) => m.name).join(", ") ||
    "Not specified";
  const secondaryMuscles =
    exercise.secondaryTargetMuscles?.map((m: any) => m.name).join(", ") ||
    "Not specified";
  const categoryNames =
    exercise.categories?.map((c: any) => c.title).join(", ") || "No categories";
  const programNames =
    exercise.programs?.map((p: any) => p.title).join(", ") || "No programs";
  const equipmentNames =
    exercise.equipment?.map((e: any) => e.name).join(", ") || "No equipment";

  const getGenderLabels = (genders: string[] | undefined) => {
    const labels: Record<string, string> = {
      male: "Male",
      female: "Female",
    };
    return genders && genders.length > 0
      ? genders.map((gender) => labels[gender] || gender).join(", ")
      : "Not specified";
  };

  // const getFitnessGoalLabels = (goals: string[] | undefined) => {
  //     const labels: Record<string, string> = {
  //         build_muscles: 'Build Muscles',
  //         lose_weight: 'Lose Weight',
  //     };
  //     return goals && goals.length > 0
  //         ? goals.map(goal => labels[goal] || goal).join(', ')
  //         : 'Not specified';
  // };

  const getMoodLabels = (moods: string[] | undefined) => {
    const labels: Record<string, string> = {
      energetic: "Energetic",
      normal: "Normal",
      tired: "Tired",
    };
    return moods && moods.length > 0
      ? moods.map((mood) => labels[mood] || mood).join(", ")
      : "Not specified";
  };

  const getFormattedFitnessLevels = (levels: string[] | undefined) => {
    if (!levels || levels.length === 0) return "Not specified";
    return levels
      .map((level) => level.charAt(0).toUpperCase() + level.slice(1))
      .join(", ");
  };

  // const fitnessGoal = getFitnessGoalLabels(exercise.fitnessGoal);
  const fitnessMood = getMoodLabels(exercise.mood);
  const gender = getGenderLabels(exercise.gender);

  console.log("exercise", exercise);

  const getReadableExerciseType = (type: string | null) => {
    switch (type) {
      case "weight_and_reps_based":
        return "Weight and Reps Based";
      case "time_based":
        return "Time Based";
      case "distance_based":
        return "Distance Based";
      default:
        return "Not specified";
    }
  };

  const getReadableFitnessGoal = (goals: string[] | undefined) => {
    if (!goals || goals.length === 0) return "Not specified";

    const labels: Record<string, string> = {
      "build-muscles": "Build Muscles",
      "lose-weight": "Lose Weight",
    };

    return goals.map((goal) => labels[goal] || "Not specified").join(", ");
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <Header title="Exercise Details" />
      <div className="flex items-center justify-between mt-4 border-b pb-4">
        <BackButton onClick={() => navigate("/exercises")} />
      </div>
      <div className="mt-6 grid grid-cols-3 gap-6">
        <div className="col-span-3 sm:col-span-1">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Name
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2">
              {exercise.name || "Not specified"}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Instructions
            </label>
            <div
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2"
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                wordWrap: "break-word",
                whiteSpace: "pre-wrap",
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  exercise.description || "Not specified"
                ),
              }}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Exercise Type
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2">
              {getReadableExerciseType(exercise.exerciseType) ||
                "Not specified"}
            </div>
          </div>

          <div className="mt-4 bg-white p-3 rounded-lg shadow-md border">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Working Sets
            </h3>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 text-xs mb-2">
              <span className="font-semibold text-gray-700">Set</span>
              {exercise.exerciseType === "weight_and_reps_based" && (
                <>
                  <span className="font-semibold text-gray-700">Kg</span>
                  <span className="font-semibold text-gray-700">Reps</span>
                </>
              )}
              {exercise.exerciseType === "time_based" && (
                <span className="font-semibold text-gray-700">
                  Time (seconds)
                </span>
              )}
              {exercise.exerciseType === "distance_based" && (
                <span className="font-semibold text-gray-700">
                  Distance (meters)
                </span>
              )}
            </div>

            {exercise.workingSets && exercise.workingSets.length > 0 ? (
              exercise.workingSets.map((set: any, index: number) => (
                <div
                  key={index}
                  className={`grid grid-cols-3 sm:grid-cols-${
                    exercise.exerciseType === "weight_and_reps_based" ? 4 : 3
                  } gap-2 items-center mb-1`}
                >
                  <div className="text-center">{set.set}</div>
                  {exercise.exerciseType === "weight_and_reps_based" && (
                    <>
                      <div className="w-full border border-gray-300 rounded px-1 py-1 text-xs bg-gray-100">
                        {set.kg || "-"}
                      </div>
                      <div className="w-full border border-gray-300 rounded px-1 py-1 text-xs bg-gray-100">
                        {set.reps || "-"}
                      </div>
                    </>
                  )}
                  {exercise.exerciseType === "time_based" && (
                    <div className="w-full border border-gray-300 rounded px-1 py-1 text-xs bg-gray-100">
                      {set.time || "-"}
                    </div>
                  )}
                  {exercise.exerciseType === "distance_based" && (
                    <div className="w-full border border-gray-300 rounded px-1 py-1 text-xs bg-gray-100">
                      {set.meters || "-"}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm">
                No working sets available
              </div>
            )}
          </div>

          <div className="mb-4 mt-4">
            <label className="block text-sm font-medium text-gray-700">
              Equipment
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2">
              {equipmentNames}
            </div>
          </div>
        </div>

        <div className="col-span-3 sm:col-span-1">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Categories
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2">
              {categoryNames}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Programs
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2">
              {programNames}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Target Muscles
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2">
              {primaryMuscles}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Secondary Target Muscles
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2">
              {secondaryMuscles}
            </div>
          </div>
        </div>

        <div className="col-span-3 sm:col-span-1">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Gender
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2">
              {gender}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Fitness Level
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2 capitalize">
              {getFormattedFitnessLevels(exercise.fitnessLevel)}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Priority
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2 capitalize">
              {exercise.priority || "Not specified"}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Fitness Mood
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2">
              {fitnessMood}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Fitness Goal
            </label>
            <div className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-100 p-2">
              {getReadableFitnessGoal(exercise.fitnessGoal)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseDetails;
